import {
  CalendarDays,
  ChevronDown,
  ExternalLink,
  FileText,
  Loader2,
  Paperclip,
  Pencil,
  Target,
  Trash2,
} from "lucide-react";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MONTHS_SHORT } from "@/lib/date";
import { cn } from "@/lib/utils";

import {
  useDeleteAttachment,
  useDeleteObjective,
  useUpdateObjective,
  useUploadAttachment,
  type Objective,
  type ObjectiveStatus,
} from "../api";
import { ownerColor } from "../constants";
import { MilestoneList } from "./MilestoneList";
import { NotesField } from "./NotesField";
import { StatusPill } from "./StatusPill";

export function ObjectiveCard({
  objective,
  canWrite,
  onEdit,
}: {
  objective: Objective;
  canWrite: boolean;
  onEdit: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const update = useUpdateObjective();
  const remove = useDeleteObjective();
  const accent = ownerColor(objective.owner_id);

  const showDefinition = !!objective.definition_of_success;
  const hasPlanContent =
    !!objective.plan_url || !!objective.plan_body || objective.attachments.length > 0;
  const showPlan = hasPlanContent || canWrite;
  const defaultTab = showDefinition ? "definition" : hasPlanContent ? "plan" : "milestones";

  return (
    <article
      className="rounded-lg border border-border bg-card"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      <div className="flex items-start gap-3 p-4">
        <div
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-border bg-muted/40 text-xs font-bold"
          style={{ color: accent }}
        >
          P{objective.priority}
        </div>

        <button
          type="button"
          className="flex flex-1 items-start gap-2 text-left"
          onClick={() => setExpanded((e) => !e)}
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold leading-snug">{objective.title}</div>
            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
              <span>{objective.owner_name ?? "Unassigned"}</span>
              <span>·</span>
              <span>
                {objective.milestones.length} milestone
                {objective.milestones.length === 1 ? "" : "s"}
              </span>
              {objective.target_date && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" /> {fmtTargetDate(objective.target_date)}
                  </span>
                </>
              )}
              {objective.goal_title && (
                <>
                  <span>·</span>
                  <span
                    className="inline-flex items-center gap-1 text-foreground/70"
                    title="Linked company goal"
                  >
                    <Target className="h-3 w-3" /> {objective.goal_title}
                  </span>
                </>
              )}
            </div>
            <ProgressBar pct={objective.progress_pct} accent={accent} />
          </div>
        </button>

        <div className="flex flex-shrink-0 items-center gap-2">
          <StatusPill
            value={objective.status}
            readOnly={!canWrite}
            onChange={(s: ObjectiveStatus) =>
              update.mutate({ id: objective.id, payload: { status: s } })
            }
          />
          <ChevronDown
            className={cn(
              "h-4 w-4 cursor-pointer text-muted-foreground transition-transform",
              expanded && "rotate-180",
            )}
            onClick={() => setExpanded((e) => !e)}
          />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border px-4 py-4">
          {canWrite && (
            <div className="mb-3 flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" className="gap-1" onClick={onEdit}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 text-destructive hover:text-destructive"
                onClick={() => {
                  if (confirm(`Delete objective “${objective.title}”? This removes its milestones and attachments.`))
                    remove.mutate(objective.id);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </Button>
            </div>
          )}

          <Tabs defaultValue={defaultTab} className="w-full">
            <TabsList variant="underline" className="w-full justify-start">
              {showDefinition && <TabsTrigger value="definition">Definition</TabsTrigger>}
              {showPlan && <TabsTrigger value="plan">Plan</TabsTrigger>}
              <TabsTrigger value="milestones" count={objective.milestones.length}>
                Milestones
              </TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            {showDefinition && (
              <TabsContent value="definition" className="pt-3">
                <p className="whitespace-pre-wrap text-sm">{objective.definition_of_success}</p>
              </TabsContent>
            )}

            {showPlan && (
              <TabsContent value="plan" className="pt-3">
                <PlanPanel objective={objective} canWrite={canWrite} />
              </TabsContent>
            )}

            <TabsContent value="milestones" className="pt-3">
              <MilestoneList objective={objective} canWrite={canWrite} />
            </TabsContent>

            <TabsContent value="notes" className="pt-3">
              <NotesField
                value={objective.notes ?? ""}
                disabled={!canWrite}
                placeholder="Status update, blockers, next steps…"
                onSave={(v) => update.mutate({ id: objective.id, payload: { notes: v } })}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </article>
  );
}

// Parse a YYYY-MM-DD string as a local date (no UTC shift) and format readably.
function fmtTargetDate(d: string): string {
  const [y, m, day] = d.split("-").map(Number);
  if (!y || !m || !day) return d;
  return `${MONTHS_SHORT[m - 1]} ${day}, ${y}`;
}

function ProgressBar({ pct, accent }: { pct: number; accent: string }) {
  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: accent }}
        />
      </div>
      <span className="w-10 text-right text-xs font-semibold tabular-nums text-muted-foreground">
        {Math.round(pct)}%
      </span>
    </div>
  );
}

function PlanPanel({ objective, canWrite }: { objective: Objective; canWrite: boolean }) {
  const upload = useUploadAttachment();
  const removeAtt = useDeleteAttachment();
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <div className="flex flex-col gap-2">
      {objective.plan_url && (
        <a
          href={objective.plan_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex w-fit items-center gap-1 text-sm text-brand hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" /> {objective.plan_url}
        </a>
      )}

      {objective.plan_body && (
        // Cap the height so a long pasted plan scrolls inside the card
        // instead of expanding it indefinitely.
        <div
          className="rich-text max-h-80 overflow-y-auto rounded-md border border-border bg-background p-3"
          dangerouslySetInnerHTML={{ __html: objective.plan_body }}
        />
      )}

      {objective.attachments.length > 0 && (
        <ul className="flex flex-col gap-1">
          {objective.attachments.map((a) => (
            <li key={a.id} className="flex items-center gap-2 text-sm">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <a href={a.url} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                {a.original_filename}
              </a>
              <span className="text-xs text-muted-foreground">
                {a.extracted_text_status === "ok"
                  ? "· text ready"
                  : a.extracted_text_status === "unsupported"
                    ? "· no text"
                    : a.extracted_text_status === "failed"
                      ? "· extract failed"
                      : ""}
              </span>
              {canWrite && (
                <button
                  type="button"
                  onClick={() =>
                    removeAtt.mutate({ objectiveId: objective.id, attachmentId: a.id })
                  }
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Remove attachment"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canWrite && (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,.doc,.txt,.md,image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate({ objectiveId: objective.id, file });
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1 text-xs"
            disabled={upload.isPending}
            onClick={() => fileRef.current?.click()}
          >
            {upload.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Paperclip className="h-3.5 w-3.5" />
            )}
            Attach plan file
          </Button>
        </div>
      )}
    </div>
  );
}
